import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, Camera as CameraIcon } from "lucide-react";

/**
 * Loading + error overlay shown over the AR mirror until the camera
 * stream + MediaPipe FaceLandmarker are both ready.
 *
 * Loading is broken into 3 visible stages so the user always knows
 * what's happening rather than staring at one long spinner — each
 * stage advances the message *and* a 3-dot progress indicator. The
 * stage is set by the parent LiveCoach as it walks through its init
 * sequence:
 *
 *   "model"   → MediaPipe WASM + face landmark model loading
 *   "camera"  → camera permission + stream initialising
 *   "warming" → first inference frame, settling 30 FPS RAF loop
 *   "ready"   → mirror is live (overlay hides)
 *
 * Copy intentionally keeps the "drag mom in the mirror" line — it
 * lands the brand voice on the most-watched moment of the app.
 */
const STAGE_COPY = {
  model: {
    title: "Loading your drag mom\nin the mirror…",
    body: "Pulling on her lashes. First-launch only — everything stays on your device after this.",
    activeDot: 0,
  },
  camera: {
    title: "Cueing your camera…",
    body: "Trouvé needs to see your face — give her a second to focus.",
    activeDot: 1,
  },
  warming: {
    title: "She's almost in position…",
    body: "Last touch of highlight. Find good light if you can.",
    activeDot: 2,
  },
};

const ProgressDots = ({ activeDot }) => (
  <div
    className="flex items-center justify-center gap-2 mb-5"
    aria-hidden="true"
  >
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className={`h-1.5 rounded-full transition-all duration-500 ${
          i === activeDot
            ? "w-8 bg-[#FFE6D7]"
            : i < activeDot
              ? "w-1.5 bg-[#D5A8A4]"
              : "w-1.5 bg-white/20"
        }`}
        data-testid={`mirror-progress-${i}`}
      />
    ))}
  </div>
);

const LiveCoachStatusOverlay = ({
  phase,
  errorMsg,
  onClose,
  onRetry,
  onContinueTextOnly,
  // Sub-stage during phase === "loading". Defaults to "model" so the
  // user sees the warmest greeting first.
  loadingStage = "model",
}) => {
  const { t } = useTranslation();
  const stage = STAGE_COPY[loadingStage] || STAGE_COPY.model;

  return (
    <AnimatePresence>
      {phase !== "ready" && phase !== "text_only" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 z-20 flex items-center justify-center px-6 bg-black/65 backdrop-blur"
          data-testid="live-coach-status"
        >
          <div className="max-w-sm text-center">
            <motion.div
              animate={{
                rotate: phase === "loading" ? [0, 8, -8, 0] : 0,
                scale: phase === "loading" ? [1, 1.06, 1] : 1,
              }}
              transition={{
                duration: 2.2,
                repeat: phase === "loading" ? Infinity : 0,
                ease: "easeInOut",
              }}
              className="h-16 w-16 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#9B6A6C]/30"
            >
              {phase === "loading" ? (
                <Sparkles className="h-7 w-7 text-white" />
              ) : (
                <CameraIcon className="h-6 w-6 text-white" />
              )}
            </motion.div>
            {phase === "loading" ? (
              <>
                <ProgressDots activeDot={stage.activeDot} />
                <AnimatePresence mode="wait">
                  <motion.div
                    key={loadingStage}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.35 }}
                  >
                    <h3
                      className="font-serif italic text-2xl text-white mb-3 whitespace-pre-line"
                      data-testid={`mirror-loading-${loadingStage}`}
                    >
                      {stage.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed">
                      {stage.body}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <>
                <h3 className="font-serif italic text-2xl text-white mb-2">
                  Mirror unavailable
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-6">
                  {errorMsg}
                </p>
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="bg-white text-[#2C2123] rounded-full px-6 py-3 text-sm hover:bg-white/90 transition-all mb-3 mr-2"
                    data-testid="live-coach-retry"
                  >
                    {t("liveCoach.fallback.retryCamera")}
                  </button>
                )}
                {/* P5 fallback path: even when the camera or MediaPipe
                    fails, the user can still follow the routine with
                    text-only step instructions (LiveCoachStepCard
                    renders the same step text underneath this overlay).
                    Tapping this hides the overlay and lets them work
                    through the steps via the bottom card. */}
                {onContinueTextOnly && (
                  <button
                    onClick={onContinueTextOnly}
                    className="bg-[#9B6A6C] text-white rounded-full px-6 py-3 text-sm hover:bg-[#7B4A4C] transition-all mb-3 mr-2"
                    data-testid="live-coach-continue-text"
                  >
                    {t("liveCoach.fallback.continueText")}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="bg-white/15 text-white rounded-full px-6 py-3 text-sm hover:bg-white/25 transition-all"
                  data-testid="live-coach-error-close"
                >
                  Close mirror
                </button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LiveCoachStatusOverlay;
